import { ReactStripeElements } from 'react-stripe-elements'
import { useRouter } from 'next/router'

import Div from '@jarvis.oddle/core/components/Div'
import Divider from '@jarvis.oddle/core/components/Divider'
import Flex from '@jarvis.oddle/core/components/Flex'
import Form from '@jarvis.oddle/core/components/Form'
import { Column, Row } from '@jarvis.oddle/core/components/Grid'
import IconMapDragPin from '@jarvis.oddle/core/components/Icons/IconMapDragPin'
import { H2, LinkText, P } from '@jarvis.oddle/core/components/Typo'
import { orderStatuses } from '@jarvis.oddle/core/tools/order'
import { TranslationContext } from '@jarvis.oddle/core/components/Translation'
import { colors, margin, marginSizes } from '@jarvis.oddle/core/theme/default'
import { createErrorString } from '@jarvis.oddle/core/tools/error'
import { getPaymentMethodError } from '@jarvis.oddle/core/tools/validation'

import modalController from 'src/decorators/ModalController'
import { isObjectEmpty } from 'src/utils/object'
import {
  getInvoiceError,
  invoiceOptions
} from '@jarvis.oddle/core/tools/paymentInvoice'
import { combineString, isEmpty } from 'src/utils/string'
import { Section } from 'src/components/molecules/CheckoutComponents/Elements'
import GoogleMapService from 'src/containers/MapService/GoogleMapService'
import UserContext from 'src/containers/UserContext'
import React, { useContext } from 'react'
import styled from 'styled-components'
import { validateCard } from 'src/utils/cardValidation'
import {
  createPaymentToken,
  paymentMethods as paymentMethodsEnum,
  paymentTypes
} from 'src/utils/payment'
import { createCheckoutPayload } from 'src/utils/voucher'
import {
  Agreement,
  CheckoutElements,
  InvoiceInformation,
  Notification,
  PaymentDetails,
  SubmitButton,
  TotalPayableSummary,
  VoucherSection
} from 'src/components/molecules/CheckoutComponents'
import useAppSelector from 'src/hooks/useAppSelector'
import {
  checkoutAction,
  PayCheckoutParams,
  selectCheckout
} from 'src/redux/checkout'
import { selectStore } from 'src/redux/stores'
import useAppDispatch from 'src/hooks/useAppDispatcher'
import {
  updateUserAction,
  updateUserDeliveryAddressAction
} from 'src/redux/user'
import { createPaymentTokenAction } from 'src/redux/paymentProcess'
import { syncDeliveryAddressTwoAction } from 'src/redux/order/action'
import { selectDetailMenuSite } from 'src/redux/meta'
import {
  getCheckoutRedirectUrl,
  getUpdatedCustomer,
  redirectPage
} from 'src/utils/checkout'
import useCustomisation from 'src/hooks/useCustomisation'

const MapWrapper = styled(Div)`
  position: relative;
  height: 220px;
  width: 100%;
`

const MapMarker = styled(IconMapDragPin)`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translateX(-50%) translateY(-100%);
`

const MapContainer = styled(Div)`
  height: 220px;
`

const StyledLinkText = styled(LinkText)`
  margin-top: ${margin[marginSizes.extraSmall]};
  white-space: nowrap;
`

const Label = styled.label`
  color: ${(props) => props.color};
`

const StyledText = styled(P)`
  margin-top: ${margin[marginSizes.none]};
`

const StyledMapSection = styled(Column)`
  margin-bottom: ${margin[marginSizes.medium]};
`

const retrieveStoresList = (availableStores) =>
  availableStores.reduce((obj, item) => ({ ...obj, [item.id]: item }), {})

interface PaymentMethods extends Oms.V4.PaymentMethodData {
  parsedId: string
}

interface CheckoutPaymentProps {
  initialValues: {
    billingAddressCheckbox: boolean
    recipientName: string
    recipientContact: string
    deliveryAddress: string
    saveCreditCard: boolean
    phone: string
    organisationName: string
    unifiedBusinessNumber: string
    recipientCheckbox: boolean
    termsAndConditionsCheckbox: boolean
    newsletterCheckbox: boolean
    additionalRemarks: string
    cardName: string
    card: Record<string, unknown>
    stripeRequestPaymentToken: string
    stripeRequestPaymentButtonType: string
    stripeRequestPaymentResponse: string
    carrierType: { value: string; label: string }
    carrierNumber: string
  }
  voucherSummary: {
    voucherType: string
    voucherTitle: string
    redeemedAmount: number
  }
  onClickBackButton: () => void
  paymentMethods: PaymentMethods[]
  initPaymentMethod: PaymentMethods
  stripePaymentRequestObject: {
    canMakePayment: () => Record<string, boolean>
    on: (x, y) => void
    show: () => void
  }
  paymentProcessOverlayRef: React.RefObject<{ setShouldShow? }>
  isFullyPaidByVoucher: boolean
  setShowWarningBeforeLeaving: (x) => void
  passingProps: Record<string, unknown>
}

interface Summary extends CheckoutPaymentProps {
  MapComponent
  getMapProps
  mapApi
}

const Summary = ({
  MapComponent,
  getMapProps,
  voucherSummary,
  onClickBackButton,
  initialValues,
  paymentMethods,
  initPaymentMethod,
  stripePaymentRequestObject,
  paymentProcessOverlayRef,
  isFullyPaidByVoucher,
  setShowWarningBeforeLeaving,
  passingProps
}: Summary): JSX.Element => {
  const { t } = useContext(TranslationContext)
  const router = useRouter()
  const dispatch = useAppDispatch()

  const checkoutState = useAppSelector(selectCheckout)
  const metaState = useAppSelector(selectDetailMenuSite)
  const storeState = useAppSelector(selectStore)
  const { getSectionFromId } = useCustomisation()

  const { checkoutPageBodyFontColor } =
    getSectionFromId('checkoutPage').settings || {}

  const storeOrder = {
    data: checkoutState.meta.data.storeOrder || ({} as Oms.V4.StoreOrder)
  }
  const user = { data: checkoutState.meta.data.customer }
  const { receiveEmail, acceptMarketingMenu } = user.data || {}
  const selectedVoucher = checkoutState.selectedVoucher.data
  const invoiceOption = checkoutState.meta.data.invoiceOption

  const mapOptions = {
    draggable: false,
    zoomControl: false,
    scrollwheel: false,
    disableDoubleClickZoom: true,
    fullscreenControl: false,
    clickableIcons: false,
    draggableCursor: 'default'
  }
  const isPickup = storeOrder.data.isPickup
  let latitude, longitude
  const [selectedPaymentMethod, setSelectedPaymentMethod] = React.useState(
    initPaymentMethod || ({} as PaymentMethods)
  )
  const [isStripeReady, setIsStripeReady] = React.useState(false)
  const { enableUniversalPromotion } = metaState?.features || {}
  const { isEnabledCustomerSaveCreditCardOnStripe } =
    React.useContext(UserContext)

  const stripeElement =
    React.useRef<ReactStripeElements.HTMLStripeElement | null>(null)
  const stripeInstance = React.useRef<ReactStripeElements.StripeProps | null>(
    null
  )

  if (isPickup) {
    const store = retrieveStoresList(storeState.data || [])[
      storeOrder.data.storeId
    ]
    latitude = store.latitude
    longitude = store.longitude
  } else {
    latitude = storeOrder.data.customerLatitude
    longitude = storeOrder.data.customerLongitude
  }

  const onFormSubmit = async (values, { setSubmitting, setFieldError }) => {
    if (router.query.storeOrderId === 'preview') {
      return null
    }
    setShowWarningBeforeLeaving(false)
    window.localStorage.removeItem('hasRedirected')
    const { customer, storeOrder } = checkoutState.meta.data

    const {
      status: orderStatus,
      isPickup,
      deliveryAddressOne,
      deliveryAddressTwo,
      deliveryAddressState,
      deliveryAddressCity,
      deliveryAddressPostal,
      deliveryAddressCountry,
      orderPromotions
    } = storeOrder
    const { preferredDeliveryAddress, phone } = customer
    const customerPhone =
      checkoutState.meta.data?.storeOrder?.recipientContact ||
      phone ||
      values.phone
    const stripeRequestPaymentResponse = values.stripeRequestPaymentResponse
    const newPaymentWorkflow = metaState.newPaymentWorkflow
    const newStripeApi = metaState.newStripeApi
    const selectedSavedCreditCard = checkoutState.selectedSavedCreditCard.data

    const redirectUrl = getCheckoutRedirectUrl({
      storeOrderId: storeOrder.id,
      router,
      enableNewCheckoutFlow: true,
      ...passingProps
    })
    if (newPaymentWorkflow && orderStatus === orderStatuses.pending) {
      redirectPage({ redirectUrl, router, type: 'checkout' })
      return
    }

    const { token: paymentToken, tokenError } = await (async () => {
      const payload = {
        values,
        redirectUrl,
        selectedPaymentMethod,
        stripe: stripeInstance.current,
        stripeElement: stripeElement.current,
        newStripeApi,
        isFullyPaidByVoucher,
        isEnabledCustomerSaveCreditCardOnStripe,
        selectedSavedCreditCard,
        storeOrderId: storeOrder.id
      }

      if (
        newPaymentWorkflow &&
        (selectedPaymentMethod.type === paymentTypes.stripe ||
          selectedPaymentMethod.type === paymentTypes.stripeExpressAccount)
      ) {
        const response = await dispatch(createPaymentTokenAction(payload))
        if (response.meta.requestStatus === 'rejected') {
          stripeElement.current.focus()
        }

        return {
          token: response.payload,
          tokenError:
            response.meta.requestStatus === 'rejected'
              ? (response.payload as Record<string, string>)
              : null
        }
      } else {
        const { token, tokenError } = await createPaymentToken(payload)
        return { token, tokenError }
      }
    })()

    if (tokenError) {
      setFieldError('card', t(tokenError.message))
      setSubmitting(false)
      return
    }

    const payload = {
      storeOrderId: storeOrder.id,
      customerId: customer.id,
      customerEmail: customer.email,
      customerFirstName: customer.firstName,
      customerLastName: customer.lastName,
      customerContactNumber: customerPhone,
      customerCompany: customer.company,
      recipientName: values.recipientCheckbox
        ? values.recipientName
        : combineString(customer.firstName, customer.lastName),
      recipientContact: values.recipientCheckbox
        ? values.recipientContact
        : customerPhone,
      billingAddressOne: isPickup
        ? undefined
        : values.billingAddressCheckbox
        ? deliveryAddressOne
        : values.billingAddressOne,
      billingAddressTwo: isPickup
        ? undefined
        : values.billingAddressCheckbox
        ? deliveryAddressTwo
        : values.billingAddressTwo,
      billingAddressState: isPickup
        ? undefined
        : values.billingAddressCheckbox
        ? deliveryAddressState
        : values.billingAddressState,
      billingAddressCity: isPickup
        ? undefined
        : values.billingAddressCheckbox
        ? deliveryAddressCity
        : values.billingAddressCity,
      billingAddressPostal: isPickup
        ? undefined
        : values.billingAddressCheckbox
        ? deliveryAddressPostal
        : values.billingAddressPostal,
      billingAddressCountry: isPickup
        ? undefined
        : values.billingAddressCheckbox
        ? deliveryAddressCountry
        : values.billingAddressCountry,
      stripeRequestPaymentButtonType: values.stripeRequestPaymentButtonType,
      additionalInformation: values.additionalRemarks,
      paymentOption: isFullyPaidByVoucher ? null : selectedPaymentMethod.id,
      paymentOptionId: isFullyPaidByVoucher ? null : selectedPaymentMethod.id,
      paymentType: isFullyPaidByVoucher
        ? paymentTypes.oddleVoucher
        : selectedPaymentMethod.type,
      subscribeToEmail: values.newsletterCheckbox,
      unifiedBusinessNumber: values.unifiedBusinessNumber,
      organisationName: values.organisationName,
      carrierType: (values.carrierType || {}).value,
      carrierNumber: values.carrierNumber,
      paymentToken,
      redirectUrl,
      otp: null,
      version: 'V4', // required from back-end to specify the redirect url
      ...(enableUniversalPromotion
        ? {
            ...createCheckoutPayload(
              checkoutState.meta.data,
              checkoutState.selectedVoucher.data
            )
          }
        : {}),
      ...(isEnabledCustomerSaveCreditCardOnStripe &&
      selectedPaymentMethod.type === paymentTypes.stripeExpressAccount &&
      selectedPaymentMethod.paymentMethod !== paymentMethodsEnum.FPX &&
      selectedPaymentMethod.stripeRequestPaymentButtonType !==
        paymentMethodsEnum.APPLE_PAY
        ? {
            saveForFuture: !isObjectEmpty(selectedSavedCreditCard)
              ? false
              : values.saveCreditCard,
            existing: !isObjectEmpty(selectedSavedCreditCard)
          }
        : {})
    } as PayCheckoutParams

    const updateActions = []
    const updatedCustomer = getUpdatedCustomer({ customer, values })

    if (updatedCustomer) {
      updateActions.push(dispatch(updateUserAction(updatedCustomer)))
    }

    if (storeOrder.deliveryAddressTwo !== values.deliveryAddressTwo) {
      updateActions.push(
        dispatch(
          updateUserDeliveryAddressAction({
            ...preferredDeliveryAddress,
            addressTwo: values.deliveryAddressTwo as unknown as string
          })
        )
      )
      !isEmpty(values.deliveryAddressTwo) &&
        updateActions.push(
          dispatch(
            syncDeliveryAddressTwoAction({
              addressTwo: values.deliveryAddressTwo
            })
          )
        )
    }

    const updateResponse = await Promise.all(updateActions)
    const updateError = updateResponse.find(
      (response) =>
        response.meta.requestStatus === 'rejected' ||
        !isEmpty(response?.error) ||
        !isEmpty(response.payload?.errors) ||
        !isEmpty(response.payload?.payload?.error)
    )
    if (
      updateError?.error?.message &&
      updateError.error.message !== 'Rejected'
    ) {
      setFieldError('serverError', [t(updateError.error.message)])
      setSubmitting(false)

      // after Oddle Shop has successfully created a stripe source, payment source overlay
      // will still be shown until it redirects to the external payment page to prevent the page from flickering.
      // However, if a failure is happened in the middle of the checkout process, it is expected
      // to close the overlay to allow users making an amendment
      if (paymentProcessOverlayRef?.current) {
        paymentProcessOverlayRef.current.setShouldShow(false)
      }
      return
    }
    if (updateError) {
      const errors = createErrorString({
        t,
        error:
          updateError.payload?.errors || updateError.payload?.payload?.error
      })
      setFieldError('serverError', errors)
      setSubmitting(false)

      // after Oddle Shop has successfully created a stripe source, payment source overlay
      // will still be shown until it redirects to the external payment page to prevent the page from flickering.
      // However, if a failure is happened in the middle of the checkout process, it is expected
      // to close the overlay to allow users making an amendment
      if (paymentProcessOverlayRef?.current) {
        paymentProcessOverlayRef.current.setShouldShow(false)
      }
      return
    }

    const checkoutResponse = await dispatch(checkoutAction({ payload }))
    if (
      checkoutResponse.meta.requestStatus === 'fulfilled' &&
      isObjectEmpty(checkoutResponse.payload.errors)
    ) {
      const { redirect } = checkoutResponse.payload.data
      // Handle Stripe Request payment type. Let browser know the payment request is succeed so it can show the correct UI to user in payment popup
      if (stripeRequestPaymentResponse) {
        stripeRequestPaymentResponse.complete('success')
      }

      if (paymentProcessOverlayRef?.current && isEmpty(redirect)) {
        paymentProcessOverlayRef.current.setShouldShow(false)
      }

      // remove order time from localStorage after finishing checkout
      if (window.localStorage.getItem('lastTimeTouchOrder')) {
        window.localStorage.removeItem('lastTimeTouchOrder')
      }

      redirectPage({
        redirectUrl: redirect,
        router,
        type: 'checkout',
        storeOrderId: storeOrder.id
      })
    } else {
      // Handle Stripe Request payment type. Let browser know the payment request is failed so it can show the correct UI to user in payment popup
      if (stripeRequestPaymentResponse) {
        stripeRequestPaymentResponse.complete('fail')
      }

      if (paymentProcessOverlayRef?.current) {
        paymentProcessOverlayRef.current.setShouldShow(false)
      }

      const error = checkoutResponse.payload.errors

      setSubmitting(false)
      if (
        error?.find((e) =>
          ['total_amount_mismatch', 'store_order_not_found'].includes(
            e.errorCode
          )
        )
      ) {
        modalController.showModal('RefreshModal')
      } else if (
        error?.find(
          (err) =>
            err.errorCode === 'payment_card_does_not_qualify_for_promotion'
        )
      ) {
        modalController.showModal('CheckoutFailedCardPromotionModal', {
          error,
          orderPromotions,
          type: 'checkout',
          onCancelCallback: (errorMessage) => {
            /**
             * this callback will be triggered when user click the cancel button
             * on the transactional dialog
             */
            try {
              if (stripeElement) {
                stripeElement.current.focus()
                setFieldError('card', errorMessage)
              }
            } catch (e) {}
          }
        })
      } else if (
        error?.find((err) => err.errorCode === 'invalid_cellphone_barcode')
      ) {
        setFieldError(
          'carrierNumber',
          t('Barcode not found. Please enter a valid barcode')
        )
      } else if (
        error?.find((e) =>
          [
            'fail_to_redeem_voucher',
            'fail_to_calculate_redeemed_amount'
          ].includes(e.errorCode)
        )
      ) {
        const errors = createErrorString({ t, error })
        setFieldError('voucher', errors)
      } else if (error?.find((e) => ['card_declined'].includes(e.errorCode))) {
        const errors = createErrorString({ t, error })
        setFieldError('card', errors)
      } else {
        const errors = createErrorString({ t, error })
        setFieldError('serverError', errors)
      }
    }
  }

  const onFormValidate = (values) => {
    const { invoiceOption } = checkoutState.meta.data
    const {
      cardName,
      card,
      unifiedBusinessNumber,
      carrierType,
      carrierNumber,
      organisationName,
      termsAndConditionsCheckbox
    } = values

    const errors = {} as {
      termsError?: string
      paymentMethod?: string
      cardName?: string
      card?: string
    }

    if (termsAndConditionsCheckbox !== true) {
      errors.termsError = t(
        'Please accept Terms & Conditions and Privacy Policy'
      )
    }

    if (invoiceOption !== invoiceOptions.nonTW) {
      const invoiceError = getInvoiceError({
        t,
        unifiedBusinessNumber,
        carrierType,
        carrierNumber,
        organisationName
      })
      Object.entries(invoiceError).forEach(([key, value]) => {
        errors[key] = value
      })
    }

    if (!isFullyPaidByVoucher) {
      const paymentMethodError = getPaymentMethodError(t, selectedPaymentMethod)
      if (paymentMethodError) errors.paymentMethod = paymentMethodError

      if (selectedPaymentMethod.paymentMethod !== paymentMethodsEnum.FPX) {
        const cardErrors = validateCard({
          t,
          cardName,
          card,
          selectedPaymentMethod,
          isEnabledCustomerSaveCreditCardOnStripe,
          selectedSavedCreditCard: checkoutState.selectedSavedCreditCard.data
        })
        if (cardErrors.cardName) errors.cardName = cardErrors.cardName
        if (cardErrors.card) errors.card = cardErrors.card
      }
    }

    return errors
  }

  return (
    <Form
      enableReinitialize
      initialValues={initialValues}
      onSubmit={onFormSubmit}
      onValidate={onFormValidate}
    >
      <Section>
        <Row>
          <StyledMapSection sm={12} md={6}>
            <MapWrapper>
              <MapComponent
                {...getMapProps()}
                mapOptions={mapOptions}
                Container={MapContainer}
                initialCenter={{ latitude, longitude }}
              />
              <MapMarker />
            </MapWrapper>
          </StyledMapSection>
          <Column sm={12} md={6}>
            <Flex justifyContent='space-between'>
              <H2
                marginTop={marginSizes.none}
                marginLeft={marginSizes.none}
                marginRight={marginSizes.none}
              >
                {isPickup ? t('Pickup Store Address') : t('Delivery Summary')}
              </H2>
              <StyledLinkText onClick={onClickBackButton} heading>
                {t('Change Details')}
              </StyledLinkText>
            </Flex>
            {isPickup ? (
              <React.Fragment>
                <Label color={checkoutPageBodyFontColor || colors.text}>
                  {t('Store Name')}
                </Label>
                <StyledText>{storeOrder.data.storeName}</StyledText>
                <Label color={checkoutPageBodyFontColor || colors.text}>
                  {t('Store Address')}
                </Label>
                <StyledText>{storeOrder.data.storeAddress}</StyledText>
              </React.Fragment>
            ) : (
              <React.Fragment>
                <Label color={checkoutPageBodyFontColor || colors.text}>
                  {t('Delivery Address')}
                </Label>
                <StyledText>{initialValues.deliveryAddress}</StyledText>
              </React.Fragment>
            )}
            <Label color={checkoutPageBodyFontColor || colors.text}>
              {t("Recipient's Name")}
            </Label>
            <StyledText>
              {isEmpty(initialValues.recipientName)
                ? combineString(user.data.firstName, user.data.lastName)
                : initialValues.recipientName}
            </StyledText>
            <Label color={checkoutPageBodyFontColor || colors.text}>
              {t('Contact Number')}
            </Label>
            <StyledText>
              {initialValues.recipientContact ||
                storeOrder.data.recipientContact ||
                user.data.phone}
            </StyledText>
          </Column>
        </Row>
      </Section>
      <Divider />
      <InvoiceInformation invoiceOption={invoiceOption} />
      {enableUniversalPromotion && (
        <VoucherSection
          selectedVoucher={selectedVoucher}
          storeOrderId={storeOrder.data.id}
        />
      )}
      <CheckoutElements.Section>
        {!isFullyPaidByVoucher && (
          <PaymentDetails
            onStripeReady={(element) => {
              stripeElement.current = element
              setIsStripeReady(true)
            }}
            onStripeLoaded={(instance) => {
              stripeInstance.current = instance
            }}
            paymentMethods={paymentMethods}
            selectedPaymentMethod={selectedPaymentMethod}
            onSelectPaymentMethod={(payment) => {
              setSelectedPaymentMethod(payment)
              setIsStripeReady(
                payment.type !== paymentTypes.stripe &&
                  payment.type !== paymentTypes.stripeExpressAccount
                  ? false
                  : isStripeReady
              )
            }}
            isEnabledCustomerSaveCreditCardOnStripe={
              isEnabledCustomerSaveCreditCardOnStripe
            }
            selectedSavedCreditCard={checkoutState.selectedSavedCreditCard.data}
          />
        )}
        <Agreement
          receiveEmail={
            acceptMarketingMenu !== null && acceptMarketingMenu !== undefined
              ? acceptMarketingMenu
              : receiveEmail
          }
        />
        <Notification showAgreement />
      </CheckoutElements.Section>
      {enableUniversalPromotion && (
        <TotalPayableSummary
          isFullyPaidByVoucher={isFullyPaidByVoucher}
          storeOrder={storeOrder}
          voucherSummary={voucherSummary}
        />
      )}
      <SubmitButton
        buttonText={t('Place Order')}
        selectedPaymentMethod={selectedPaymentMethod}
        stripePaymentRequestObject={stripePaymentRequestObject}
        isStripeReady={isStripeReady}
        mobileScaleFullWidth
        isEnabledCustomerSaveCreditCardOnStripe={
          isEnabledCustomerSaveCreditCardOnStripe
        }
        selectedSavedCreditCard={checkoutState.selectedSavedCreditCard.data}
      />
    </Form>
  )
}

const CheckoutPayment = (props: CheckoutPaymentProps): JSX.Element => {
  return (
    <React.Fragment>
      <GoogleMapService>
        {({ mapApi, getMapProps, MapComponent }) => (
          <Summary
            mapApi={mapApi}
            getMapProps={getMapProps}
            MapComponent={MapComponent}
            {...props}
          />
        )}
      </GoogleMapService>
    </React.Fragment>
  )
}

export default CheckoutPayment
